import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { track } from '../lib/analytics'

// Signup posts to the `subscribe` edge function, which holds the beehiiv API key
// server-side. The key must never ship in this bundle.
const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscribe`
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

// A hung request otherwise sits until the browser itself gives up. We lost a real
// signup that way (2026-07-24 00:09, Facebook in-app browser): submit -> 25s of
// nothing -> a THROWN fetch, so no HTTP status ever came back and someone who had
// already typed their address never got on the list.
//
// Two defences: cap the wait, and retry once. A cold Supabase edge function is
// exactly the case a single retry fixes.
//
// Retrying a POST is safe HERE because beehiiv signup is keyed on the email
// address, so a duplicate is an upsert rather than a second subscriber. Don't copy
// this retry onto a non-idempotent endpoint.
// 7s, not 10s, because the timeout COMPOUNDS with the retry: two attempts is the
// number the user actually waits. At 10s that is a 20s worst case, barely better
// than the 25s hang that lost the signup in the first place. 7s x 2 caps the wait
// at ~14s while still leaving room for a slow-but-working mobile connection.
const TIMEOUT_MS = 7_000
const RETRIES = 1

async function postSubscribe(email: string): Promise<Response> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS)
  try {
    return await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ANON_KEY}`,
        apikey: ANON_KEY,
      },
      body: JSON.stringify({ email }),
      signal: ctrl.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

type State = 'idle' | 'sending' | 'done' | 'error'

interface Props {
  /** 'bar' is the inline desktop top-bar form; 'stacked' is the mobile version. */
  variant?: 'bar' | 'stacked'
  /** Fires once on a successful signup. The subscribe modal uses it to swap its
      whole body to a thank-you view; the inline form ignores it and shows its
      own "You're on the list" line. */
  onSuccess?: () => void
}

export function SubscribeForm({ variant = 'bar', onSuccess }: Props) {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')
  const [message, setMessage] = useState('')
  const stacked = variant === 'stacked'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (state === 'sending') return
    setState('sending')
    setMessage('')
    // Never send the address itself to analytics; the placement is what's useful.
    track('subscribe_submitted', { placement: variant })
    let attempts = 0
    try {
      let res: Response
      // Retry only a THROWN fetch (timeout / connection drop). An HTTP error is a
      // real answer from the server and gets reported, not retried.
      for (;;) {
        attempts++
        try {
          res = await postSubscribe(email)
          break
        } catch (err) {
          if (attempts > RETRIES) throw err
        }
      }
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setState('error')
        setMessage(body?.error ?? 'Could not sign you up just now.')
        track('subscribe_failed', { placement: variant, status: res.status, attempts })
        return
      }
      setState('done')
      setEmail('')
      track('subscribe_succeeded', { placement: variant, attempts })
      onSuccess?.()
    } catch (err) {
      const timedOut = err instanceof DOMException && err.name === 'AbortError'
      setState('error')
      setMessage(
        timedOut
          ? 'That took too long. Please try again.'
          : 'Could not reach the server. Please try again.',
      )
      // Carry WHAT failed, not just that it did. The old bare `catch {}` threw the
      // error away, which made every network-class failure look identical and left
      // nothing to debug from.
      track('subscribe_failed', {
        placement: variant,
        status: timedOut ? 'timeout' : 'network',
        message: String(err),
        attempts,
      })
    }
  }

  if (state === 'done') {
    return (
      // Brick check, slate words - the confirmation the rest of the site uses.
      // Both render sites are light (the subscribe modal's white panel and the
      // sidebar's paper card), so slate-700 holds its contrast in each.
      <p
        className={`flex items-center gap-1.5 text-sm font-medium text-slate-700 ${
          stacked ? '' : 'justify-center'
        }`}
      >
        <Check className="h-4 w-4 shrink-0 text-brick-600" />
        You&apos;re on the list. Check your inbox.
      </p>
    )
  }

  return (
    <div className={stacked ? 'w-full' : 'w-full'}>
      <form
        onSubmit={submit}
        className={stacked ? 'flex flex-col gap-2' : 'flex w-full items-center gap-2'}
      >
        <label htmlFor={`sub-${variant}`} className="sr-only">
          Email address
        </label>
        <input
          id={`sub-${variant}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brick-500 focus:outline-none focus:ring-1 focus:ring-brick-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-brick-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brick-700 disabled:opacity-60 ${
            stacked ? 'w-full' : ''
          }`}
        >
          {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
          Subscribe
        </button>
      </form>
      {state === 'error' && (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{message}</p>
      )}
    </div>
  )
}
